import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Factory, MapPin, ShieldCheck } from "lucide-react";
import { scrollToId, ease } from "./constants";
import { LogoMark } from "./Icons";

const LINES = [
  { t: "Compensados" },
  { t: "e madeiras" },
  { t: "com preço direto", y: true },
  { t: "da fábrica", y: true },
];

const HIGHLIGHTS = [
  { icon: Factory, t: "Preço direto da fábrica" },
  { icon: MapPin, t: "Atendimento em Manaus" },
  { icon: ShieldCheck, t: "Qualidade e confiança" },
];

const Badge = ({ rx, ry }) => (
  <div className="relative hidden lg:flex items-center justify-center [perspective:1200px]">
    <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.7, ease }} className="relative h-[340px] w-[340px]">
      <div className="absolute inset-0" style={{ transform: "translateZ(20px)" }}>
      <motion.svg viewBox="0 0 200 200" className="h-full w-full" animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: "linear" }}>
        <defs><path id="circ" d="M100,100 m-88,0 a88,88 0 1,1 176,0 a88,88 0 1,1 -176,0" /></defs>
        <text className="font-mono-x" fill="#FACC15" fontSize="9.5" letterSpacing="4.2">
          <textPath href="#circ">DESTAK • COMPENSADOS & MADEIRAS • MANAUS – AM • </textPath>
        </text>
      </motion.svg>
      </div>
      <div style={{ transform: "translateZ(60px)" }} className="absolute inset-[42px] rounded-full shadow-[0_40px_80px_-20px_rgba(250,204,21,0.45)]">
        <LogoMark className="h-full w-full" />
      </div>
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [16, -16]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), { stiffness: 110, damping: 16 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="inicio" ref={ref} onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden bg-[#0c0a09]" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-x-0 -top-[4%] h-[112%]">
        <motion.img initial={{ scale: 1.25 }} animate={{ scale: 1.06 }} transition={{ duration: 2.6, ease }} src="/img/hero.jpg" alt="Estoque de compensados e madeiras" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0c0a09]/95 via-[#0c0a09]/70 to-[#0c0a09]/30" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0c0a09] to-transparent" />
      <div className="grain absolute inset-0" />
      <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 1.4, delay: 0.2, ease }} className="absolute left-0 top-0 h-full w-1.5 origin-top bg-[#FACC15]" />

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-10 pt-32 sm:px-8 sm:pb-14">
        <div className="grid items-center lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="font-mono-x mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#FACC15]">
              <span className="h-px w-10 bg-[#FACC15]" /> Manaus — Amazonas
            </motion.p>
            <h1 className="font-display text-[clamp(2.9rem,12.5vw,6.2rem)] font-black uppercase leading-[0.86] tracking-tight text-white" data-testid="hero-title">
              {LINES.map((l, i) => (
                <span key={l.t} className="block overflow-hidden pt-[0.08em] -mt-[0.04em]">
                  <motion.span className={`block ${l.y ? "text-[#FACC15]" : ""}`} initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 1.15, delay: 0.3 + i * 0.12, ease }}>
                    {l.t}
                  </motion.span>
                </span>
              ))}
            </h1>
          </div>
          <div className="lg:col-span-4"><Badge rx={rx} ry={ry} /></div>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.95, ease }} className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <p className="max-w-md text-base text-stone-300 sm:text-lg" data-testid="hero-subtitle">
            Qualidade, variedade e preços competitivos para sua obra, empresa ou projeto em Manaus.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button onClick={() => scrollToId("produtos")} data-testid="hero-ver-produtos-btn" className="btn-yellow btn-lg">
              Ver produtos <ArrowDownRight className="h-5 w-5" />
            </button>
            <button onClick={() => scrollToId("contato")} data-testid="hero-pedir-orcamento-btn" className="btn-black btn-lg">
              Pedir orçamento
            </button>
          </div>
        </motion.div>

        <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }} className="mt-10 grid grid-cols-1 gap-3 border-t border-white/15 pt-6 sm:grid-cols-3" data-testid="hero-highlights">
          {HIGHLIGHTS.map(({ icon: Icon, t }) => (
            <li key={t} className="flex items-center gap-3 text-sm text-stone-200">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#FACC15]/50 text-[#FACC15]"><Icon className="h-4 w-4" /></span>
              {t}
            </li>
          ))}
        </motion.ul>
      </motion.div>
    </section>
  );
};
